/**
 * This component serves as the root of your application and should typically be the only
 * component subscribed to the store.
 *
 * It is also a good place to fetch the current user. Once you have configured 'models/currentUser'
 * to fetch the current user (by pointing it to the correct API endpoint) uncomment the commented
 * out code below to fetch the user, display a loading experience while they're being
 * fetched, and store the user in the applications context so that components can retrieve it.
 */

import React from 'react';
import {useConnect} from '@lore/query-connect';
import * as Sentry from '@sentry/browser';
import {PayloadStates} from '@lore/utils';
import {Navigate, Outlet} from 'react-router-dom';
import _ from 'lodash';

import {UserContext} from '@lore/auth';
import {useConfig} from '@lore/config';
import {DialogProvider} from '@lore/dialogs';

import {useDebug} from '@/hooks/useDebug';
import useAccountSetup from '@/hooks/useAccountSetup';
import useRouter from '@/hooks/useRouter';
import useTags from '@/hooks/useTags';
import useTagTrees from '@/hooks/useTagTrees';
import NetworkContext from '@/context/NetworkContext';
import isBlacklistedRedirectUrl from '@/utils/isBlacklistedRedirectUrl';
import storage from '@/utils/storage';
import DialogLauncherLayout from '@/dialogs-routable/_launcher/Layout';

import FullStoryUser from './FullStoryUser';
import GoogleUser from './GoogleUser';
import Impersonation from './Impersonation';
import RemoveLoadingScreen from './RemoveLoadingScreen';
import SentryError from './SentryError';
import SentryUser from './SentryUser';

export default function Master(_props) {
  const {location} = useRouter();
  const config = useConfig();
  const [debug, _setDebug] = useDebug()

  const currentUser = useConnect('currentUser.singleton');
  const {
    isFetching: isFetchingAccountSetup,
    isAccountSetupComplete,
    streamlinedAccountFlowEnabled,
    hasName,
    hasZipCode,
    hasIntents,
    isEducator,
    hasSubjects,
    hasSites
  } = useAccountSetup(currentUser);

  const networks = useConnect('network.find', {pagination: {pageSize: 40}});

  const tags = useTags();
  const tagTrees = useTagTrees();

  function reportError(error, errorInfo) {
    if (config.raven.enabled) {
      Sentry.withScope(scope => {
        Object.keys(errorInfo).forEach(key => {
          scope.setExtra(key, errorInfo[key]);
        });
        Sentry.captureException(error);
      });
    }
  }

  if (currentUser.state === PayloadStates.ERROR_FETCHING) {
    if (currentUser.error && currentUser.error.statusCode === 401) {
      const redirectUrl = `${window.location.pathname}${window.location.search}`;

      if (!isBlacklistedRedirectUrl(redirectUrl)) {
        storage.set('redirectUrl', redirectUrl);
      }

      return (
        <>
          <RemoveLoadingScreen/>
          <Navigate to="/logout"/>
        </>
      );
    }

    reportError(new Error('Master:err'), currentUser);

    return (
      <div>
        <RemoveLoadingScreen/>
        <SentryError/>
      </div>
    );
  }

  if (
    isFetchingAccountSetup ||
    currentUser.state === PayloadStates.FETCHING ||
    networks.state === PayloadStates.FETCHING ||
    tags.state === PayloadStates.FETCHING ||
    tagTrees.state === PayloadStates.FETCHING
  ) {
    return null;
  }

  // effectiveNetworkId is intentional here — legacy pages require a network even for new users
  // who will never have one, so we fall through to effectiveNetworkId before networkId.
  const networkId = currentUser.data.effectiveNetworkId || currentUser.data.networkId || 0;
  const network = _.find(networks.data, a => a.id === networkId);

  let networkRedirect
  let accountRedirect
  if (streamlinedAccountFlowEnabled === true) {
    if (!isAccountSetupComplete) {
      accountRedirect = location.pathname !== '/setup/account'
    }
  }
  else if (streamlinedAccountFlowEnabled === false) {
    networkRedirect = !currentUser.data.networkId && !(
      location.pathname === '/setup/network' ||
      location.pathname === '/setup/unavailable' ||
      location.pathname === '/setup/thanks'
    );
  }

  const profileRedirect = false;

  const inactiveRedirect = currentUser.data.inactive && !(
    location.pathname === '/inactive'
  );

  if (inactiveRedirect) {
    return (
      <Navigate to="/inactive"/>
    );
  }

  return (
    <>
      {debug && <details className="absolute top-4 left-0 text-xs z-40">
        <summary className="cursor-pointer">&#128681;</summary>
        <pre className="bg-cs-blue-100">
          account setup complete: {isAccountSetupComplete ? 'yes' : 'no'}
          <br/> - has name: {hasName ? 'yes' : 'no'}
          <br/> - has zip: {hasZipCode ? 'yes' : 'no'}
          <br/> - has intents: {hasIntents ? 'yes' : 'no'}
          <br/> - is educator: {isEducator ? 'yes' : 'no'}
          {isEducator && (
            <>
              <br/> - has subjects: {hasSubjects ? 'yes' : 'no'}
              <br/> - has sites: {hasSites ? 'yes' : 'no'}
            </>
          )}
          <br/> feature flag: {streamlinedAccountFlowEnabled === undefined ? 'unresolved' : (streamlinedAccountFlowEnabled ? 'yes' : 'no')}
          <br/> account redirect: {accountRedirect ? 'yes' : 'no'}
          <br/> network redirect: {networkRedirect ? 'yes' : 'no'}
          <br/> profile redirect: {profileRedirect ? 'yes' : 'no'}
          <br/> {JSON.stringify({
          userID: currentUser?.data?.id,
          email: currentUser?.data?.email,
        })}
      </pre>
      </details>}
      <UserContext.Provider value={currentUser}>
        <NetworkContext.Provider value={network}>
          <RemoveLoadingScreen/>
          <SentryUser/>
          <FullStoryUser/>
          <GoogleUser/>
          <Impersonation/>
          <DialogProvider
            templates={config.dialogs.templates}
            defaultTemplate={config.dialogs.defaultTemplate}
          >
            {accountRedirect && <Navigate to="/setup/account"/>}
            {networkRedirect && <Navigate to="/setup/network"/>}
            {profileRedirect && <Navigate to="/setup/name-contact"/>}
            {!accountRedirect && !networkRedirect && !profileRedirect && <Outlet/>}
          </DialogProvider>
          <DialogLauncherLayout/>
        </NetworkContext.Provider>
      </UserContext.Provider>
    </>
  );
};
